import { useQueries, useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { createContext } from 'use-context-selector';
import { storeLocale } from '@/root/constants';
import type { StorePLP } from 'components/plpContext/types/storePLP';
import type { CollectionQuery } from 'lib/contentful/__generated__/Collection';
import { useCollectionQuery } from 'lib/contentful/__generated__/Collection';
import { useCollectionInGridContentQuery } from 'lib/contentful/__generated__/CollectionInGridContent';
import { useCollectionSectionsQuery } from 'lib/contentful/__generated__/CollectionSections';
import { useCollectionShelvesQuery } from 'lib/contentful/__generated__/CollectionShelves';
import { contentfulQueryDataSource } from 'lib/contentful/dataSources';
import { useGetCollectionQuery } from 'lib/shopify-storefront/__generated__/GetCollection';
import { storefrontQueryDataSource } from 'lib/shopify-storefront/dataSources';
import { formatContentfulCollectionQueries } from 'utils/format/collection';
import { usePreviewCookie } from 'utils/getPreviewCookie';
import { localeToCountryLanguage } from '../route/utils/localeToCountryLanguage';

export type PLPContext = StorePLP & {
  isLoading: boolean;
  handle: string;
  proCustomer: boolean;
};

export const PLPContext = createContext<PLPContext | undefined>(undefined);

export function PLPProvider({
  handle,
  children,
  proCustomer = false,
}: {
  handle: string;
  /** A `ReactNode` element. */
  children: ReactNode;
  proCustomer?: boolean;
}) {
  const { locale: localString } = useRouter();
  const locale = storeLocale(localString);
  const localeCodes = localeToCountryLanguage(locale);

  const preview = usePreviewCookie();

  const contentfulCollectionQueryVariables = {
    handle,
    locale: locale,
    preview: preview,
  };

  /**
   * Fetch collection data from Contentful.
   * - Collection query is broken out into multiple different requests in order
   *   to reduce the size and complexity of the GraphQL queries.
   * - We use `useQueries` to make the multiple requests.
   *   - Queries array is created by mapping over the query functions created
   *     by the codegen, as all queries have the same variables.
   * - We then take the array of responses to work out if any of the requests
   *   are loading, and then format the data into a single object.
   */
  const contentfulQueryHooks = [
    useCollectionQuery,
    useCollectionShelvesQuery,
    useCollectionSectionsQuery,
    useCollectionInGridContentQuery,
  ] as const;

  const contentfulCollectionQueries = useQueries({
    queries: contentfulQueryHooks.map((queryHookFunction) => {
      return {
        queryKey: queryHookFunction.getKey(contentfulCollectionQueryVariables),
        queryFn: queryHookFunction.fetcher(
          contentfulQueryDataSource({ byPassCache: true }),
          contentfulCollectionQueryVariables
        ),
      };
    }),
  });

  const contentfulIsLoading = contentfulCollectionQueries.some((query) => {
    return query.isLoading;
  });

  const [
    collectionQuery,
    collectionShelvesQuery,
    collectionSectionsQuery,
    collectionInGridContentQuery,
  ] = contentfulCollectionQueries;

  /**
   * Format the data from the various collection queries into a single object.
   * - Data from `contentfulCollectionQueries` is reduced into the shape of
   *   StorePLP.
   */
  const data = formatContentfulCollectionQueries({
    originalData: collectionQuery.data as CollectionQuery,
    originalShelvesData: collectionShelvesQuery.data,
    originalSectionsData: collectionSectionsQuery.data,
    originalInGridData: collectionInGridContentQuery.data,
  });

  const collectionQueryVariables = {
    handle,
    includeReferenceMetafieldDetails: true,
    ...localeCodes,
  };

  const { data: shopifyData, isLoading: shopifyIsLoading } = useQuery(
    useGetCollectionQuery.getKey(collectionQueryVariables),
    useGetCollectionQuery.fetcher(
      storefrontQueryDataSource(locale),
      collectionQueryVariables
    ),
    {
      select: (data) => {
        if (!data.collection) return null;
        return data;
      },
    }
  );

  const isLoading = shopifyIsLoading && contentfulIsLoading;

  // memoize the full context value
  const contextValue = useMemo(() => {
    if (data) {
      return {
        ...data,
        seo: {
          title:
            shopifyData?.collection?.seo.title ??
            shopifyData?.collection?.title ??
            data.title,
          description: shopifyData?.collection?.seo.description ?? undefined,
        },
        handle,
        proCustomer,
        isLoading,
      };
    }

    return {
      title: '',
      description: '',
      subtitle: '',
      seo: {
        title: '',
      },
      mobileImage: {
        url: '',
        title: '',
        width: 0,
        height: 0,
      },
      desktopImage: {
        url: '',
        title: '',
        width: 0,
        height: 0,
      },
      sections: [],
      shelfSections: [],
      shelves: [],
      inGridContent: [],
      handle,
      isLoading,
      proCustomer,
      backgroundColor: '',
      backgroundColorOnMobile: true,
      textTheme: 'dark' as StorePLP['textTheme'],
    };
  }, [data, handle, proCustomer, isLoading]);

  return (
    <PLPContext.Provider value={contextValue}>{children}</PLPContext.Provider>
  );
}
